export const GTM_SPA_PAGE_EVENT = 'spa_page_view';

export type ParsedGoogleAnalyticsApiValue =
  | { kind: 'gtm'; id: string }
  | { kind: 'ga4'; id: string };

export function parseGoogleAnalyticsUrl(urlField: unknown): ParsedGoogleAnalyticsApiValue | null {
  if (urlField == null) return null;
  const raw = String(urlField).trim();
  if (!raw) return null;

  const gtm = raw.match(/^GTM-([A-Z0-9]+)$/i);
  if (gtm) return { kind: 'gtm', id: `GTM-${gtm[1].toUpperCase()}` };

  const gaPref = raw.match(/^GA-([A-Z0-9]+)$/i);
  if (gaPref) return { kind: 'ga4', id: `G-${gaPref[1].toUpperCase()}` };

  const gPref = raw.match(/^G-([A-Z0-9]+)$/i);
  if (gPref) return { kind: 'ga4', id: `G-${gPref[1].toUpperCase()}` };

  return null;
}

export function isValidGtmContainerId(id: string | null | undefined): id is string {
  const p = parseGoogleAnalyticsUrl(id);
  return p !== null && p.kind === 'gtm';
}

export function isValidGa4MeasurementId(id: string | null | undefined): id is string {
  const p = parseGoogleAnalyticsUrl(id);
  return p !== null && p.kind === 'ga4';
}

function explainInvalidGtmAfterPrefix(raw: string): string {
  const original = raw;
  const t = raw.trim();
  const hints: string[] = [];
  if (original !== t) hints.push('tiene espacios al inicio o al final');
  if (!t) return hints.length ? hints.join('; ') : 'vacío';
  if (/\s/.test(t)) hints.push('sin espacios en medio');
  if (/^https?:\/\//i.test(t)) hints.push('parece URL; use solo el ID (p. ej. GTM-AB12CD3)');
  if (!/^GTM-[A-Z0-9]+$/i.test(t)) {
    hints.push('tras GTM- solo letras y números (A–Z, a–z, 0–9), sin guiones ni símbolos');
  }
  if (!hints.length) hints.push('no cumple GTM-[A-Z0-9]+');
  return hints.join('; ');
}

function explainInvalidGa4AfterPrefix(raw: string): string {
  const original = raw;
  const t = raw.trim();
  const hints: string[] = [];
  if (original !== t) hints.push('tiene espacios al inicio o al final');
  if (!t) return hints.length ? hints.join('; ') : 'vacío';
  if (/\s/.test(t)) hints.push('sin espacios en medio');
  if (/^https?:\/\//i.test(t)) hints.push('parece URL; use solo el ID');
  if (/^GTM-/i.test(t)) hints.push('es contenedor GTM-, no medición GA4');
  if (/^UA-/i.test(t)) hints.push('Universal Analytics (UA-…) no es compatible');
  if (/^GA-/i.test(t) && !/^GA-[A-Z0-9]+$/i.test(t)) {
    hints.push('tras GA- solo letras y números');
  } else if (/^G-/i.test(t) && !/^GA-/i.test(t) && !/^G-[A-Z0-9]+$/i.test(t)) {
    hints.push('tras G- solo letras y números');
  }
  if (!hints.length) hints.push('no cumple G-[A-Z0-9]+ ni GA-[A-Z0-9]+');
  return hints.join('; ');
}

export function explainRejectedGoogleAnalyticsUrl(raw: string): string {
  const t = raw.trim();
  if (!t) return 'vacío';
  if (/^UA-/i.test(t)) {
    return 'Universal Analytics (UA-…) no es compatible; use GTM-, G- o GA- con sufijo solo alfanumérico';
  }
  if (/^GTM-/i.test(t)) return explainInvalidGtmAfterPrefix(raw);
  if (/^GA-/i.test(t)) return explainInvalidGa4AfterPrefix(raw);
  if (/^G-/i.test(t)) return explainInvalidGa4AfterPrefix(raw);
  return `sin prefijo reconocido (use GTM-, G- o GA- y solo letras o números después). Ej. ${JSON.stringify('123h1h3v')} no es válido. Recibido: ${JSON.stringify(t)}`;
}

export function gtmLoaderInline(containerId: string): string {
  const idLiteral = JSON.stringify(containerId);
  return `<script>(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer',${idLiteral});</script>`;
}

export function gtmHeadEarlyFragment(containerId: string | null): string {
  const head = `<script>window.dataLayer=window.dataLayer||[];</script><script src="/gtm_debug.js"></script>`;
  if (containerId) return head + gtmLoaderInline(containerId);
  return head;
}

export function ga4GtagFragment(measurementId: string, extraMeasurementId?: string | null): string {
  const id = measurementId.trim();
  const idForUrl = encodeURIComponent(id);
  const idLit = JSON.stringify(id);
  const extraConfig =
    extraMeasurementId && extraMeasurementId !== id
      ? `gtag('config',${JSON.stringify(extraMeasurementId)});`
      : '';
  return `<script async src="https://www.googletagmanager.com/gtag/js?id=${idForUrl}"></script><script>function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config',${idLit});${extraConfig}</script>`;
}

export function analyticsHeadFragment(
  gtmId: string | null,
  ga4Id: string | null,
  opts?: { masterGtmId?: string | null; masterGa4Id?: string | null },
): string {
  const masterGtmId = opts?.masterGtmId && opts.masterGtmId !== gtmId ? opts.masterGtmId : null;
  const masterGa4Id = opts?.masterGa4Id && opts.masterGa4Id !== ga4Id ? opts.masterGa4Id : null;

  let html: string;
  if (gtmId) {
    html = gtmHeadEarlyFragment(gtmId);
    if (masterGa4Id) html += ga4GtagFragment(masterGa4Id);
  } else if (ga4Id) {
    html = gtmHeadEarlyFragment(null) + ga4GtagFragment(ga4Id, masterGa4Id);
  } else {
    html = gtmHeadEarlyFragment(null);
    if (masterGa4Id) html += ga4GtagFragment(masterGa4Id);
  }
  if (masterGtmId) html += gtmLoaderInline(masterGtmId);
  return html;
}

export function gtmNoscriptIframe(containerId: string): string {
  const q = encodeURIComponent(containerId);
  return `<noscript><iframe src="https://www.googletagmanager.com/ns.html?id=${q}" height="0" width="0" style="display:none;visibility:hidden" title=""></iframe></noscript>`;
}

export function gtmDataLayerPush(payload: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
}

export function pushConsultarPaquetesAnalytics(): void {
  if (typeof window === 'undefined') return;
  if (window.__GTM_CONTAINER_ID__) {
    gtmDataLayerPush({
      event: 'consultar_paquetes',
      event_category: 'Accion',
      event_action: 'Consultar',
      event_label: 'Paquetes',
      non_interaction: true,
    });
    return;
  }
  const ga4 = window.__GA4_MEASUREMENT_ID__;
  const tag = window.gtag as ((...args: unknown[]) => void) | undefined;
  if (ga4 && typeof tag === 'function') {
    tag('event', 'consultar_paquetes', {
      event_category: 'Accion',
      event_action: 'Consultar',
      event_label: 'Paquetes',
      non_interaction: true,
    });
  }
}
